import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, DialogActions, TextField, Typography } from '@mui/material';
import { AttachFile, OpenInNew, LinkOff, Edit } from '@mui/icons-material';
import { ControlledBubbleMenu, useRichTextEditorContext } from 'mui-tiptap';
import { getMarkRange, getMarkType } from '@tiptap/core';
import { useConfig } from '../../../../env';
import { useGuideCmsContext } from '../../GuideCmsContext';
import { theme } from '../../../../theme';
import { AttachmentMenuState } from './plugins/AttachmentBubbleMenuHandler';

export const AttachmentBubbleMenu: FC = () => {
  const editor = useRichTextEditorContext();
  const config = useConfig();
  const { guideId } = useGuideCmsContext();

  if (!editor?.isEditable || !editor?.isInitialized) {
    return null;
  }

  const menuState = editor.storage.attachmentBubbleMenuHandler?.state ?? AttachmentMenuState.HIDDEN;
  const isOpen = menuState !== AttachmentMenuState.HIDDEN;

  const attrs = editor.getAttributes('attachment');
  const attachmentId = attrs.attachmentId as string | undefined;
  const filename = attrs.filename as string | undefined;

  const downloadUrl =
    config && attachmentId && filename ? `${config.apiUrl}/guides/${guideId}/attachments/${attachmentId}/${filename}` : undefined;

  const handleRemove = () => {
    editor.chain().focus().unsetMark('attachment').run();
    editor.commands.closeAttachmentBubbleMenu();
  };

  const handleEdit = () => {
    editor.commands.editAttachmentInBubbleMenu();
  };

  const handleSave = (text: string) => {
    editor
      .chain()
      .extendMarkRange('attachment')
      .insertContent({
        type: 'text',
        marks: [
          {
            type: 'attachment',
            attrs: { attachmentId, filename },
          },
        ],
        text,
      })
      .focus()
      .run();
    editor.commands.closeAttachmentBubbleMenu();
  };

  let content = null;
  if (menuState === AttachmentMenuState.VIEW) {
    content = (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, padding: theme.spacing(0.5, 1) }}>
        <AttachFile sx={{ fontSize: '1rem', color: 'text.secondary' }} />
        <Typography variant="body2" sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {filename ?? 'Vedlegg'}
        </Typography>
        {downloadUrl && (
          <Button size="small" startIcon={<OpenInNew />} href={downloadUrl} target="_blank" rel="noopener noreferrer">
            Åpne
          </Button>
        )}
        <Button size="small" startIcon={<Edit />} onClick={handleEdit}>
          Rediger
        </Button>
        <Button size="small" startIcon={<LinkOff />} onClick={handleRemove} color="error">
          Fjern
        </Button>
      </Box>
    );
  } else if (menuState === AttachmentMenuState.EDIT) {
    content = <EditAttachmentContent editor={editor} onCancel={() => editor.commands.closeAttachmentBubbleMenu()} onSave={handleSave} />;
  }

  return (
    <ControlledBubbleMenu editor={editor} open={isOpen} sx={{ zIndex: '1000 !important' }}>
      {content}
    </ControlledBubbleMenu>
  );
};

const EditAttachmentContent: FC<{
  editor: ReturnType<typeof useRichTextEditorContext>;
  onCancel: () => void;
  onSave: (text: string) => void;
}> = ({ editor, onCancel, onSave }) => {
  const textRef = useRef<HTMLInputElement>(null);

  const markRange = editor ? getMarkRange(editor.state.selection.$from, getMarkType('attachment', editor.schema)) : undefined;
  const currentText = markRange && editor ? editor.state.doc.textBetween(markRange.from, markRange.to) : '';

  const [textValue, setTextValue] = useState(currentText);

  useEffect(() => {
    textRef.current?.focus({ preventScroll: true });
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCancel();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onCancel]);

  return (
    <Box
      component="form"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (textValue.trim()) {
          onSave(textValue);
        }
      }}
      autoComplete="off"
      sx={{ padding: theme.spacing(1.5, 2, 0.5), minWidth: 400 }}>
      <Typography variant="h6">Rediger vedlegg</Typography>
      <TextField
        inputRef={textRef}
        value={textValue}
        onChange={(e) => setTextValue(e.target.value)}
        label="Tekst"
        margin="normal"
        size="small"
        fullWidth
        required
      />
      <DialogActions sx={{ px: 0 }}>
        <Button onClick={onCancel} variant="outlined" size="small">
          Avbryt
        </Button>
        <Button type="submit" color="primary" variant="outlined" size="small">
          Lagre
        </Button>
      </DialogActions>
    </Box>
  );
};
